import type { System, World } from '../../engine/ecs';
import type { InputManager } from '../../input/input';
import { rectCollides, type TileMap } from '../../world/tilemap';
import { Collider, Facing, PlayerControlled, Position, Velocity } from '../components';

/**
 * Movimento libero (non a griglia) con collisioni AABB risolte per asse:
 * scivolare lungo i muri invece di fermarsi è ciò che rende il movimento
 * "moderno" rispetto al passo a griglia dei classici.
 */
export class MovementSystem implements System {
  readonly name = 'movement';

  constructor(
    private input: InputManager,
    private map: TileMap,
    private enabled: () => boolean,
  ) {}

  update(world: World, dt: number): void {
    const active = this.enabled();
    for (const entity of world.query(Position, Velocity, Facing, Collider, PlayerControlled)) {
      const pos = world.require(entity, Position);
      const vel = world.require(entity, Velocity);
      const facing = world.require(entity, Facing);
      const collider = world.require(entity, Collider);

      const ax = active ? this.input.axis.x : 0;
      const ay = active ? this.input.axis.y : 0;
      vel.vx = ax * vel.speed;
      vel.vy = ay * vel.speed;

      facing.walking = Math.abs(ax) > 0.01 || Math.abs(ay) > 0.01;
      if (facing.walking) {
        if (Math.abs(ax) >= Math.abs(ay)) {
          facing.dir = ax < 0 ? 'sinistra' : 'destra';
        } else {
          facing.dir = ay < 0 ? 'su' : 'giu';
        }
      }

      // collisione per asse: prova X poi Y, così si scivola lungo i muri
      const half = collider.width / 2;
      const top = (y: number) => y - collider.height;

      const nextX = pos.x + vel.vx * dt;
      if (!rectCollides(this.map, nextX - half, top(pos.y), collider.width, collider.height)) {
        pos.x = nextX;
      }
      const nextY = pos.y + vel.vy * dt;
      if (!rectCollides(this.map, pos.x - half, top(nextY), collider.width, collider.height)) {
        pos.y = nextY;
      }

      // accumula distanza per il sistema incontri
      const moved = Math.hypot(vel.vx, vel.vy) * dt;
      if (moved > 0) {
        world.require(entity, PlayerControlled).encounterMeter += moved;
      }
    }
  }
}
